import React, { Fragment, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import {
  Button,
  useDataProvider,
  useRefresh,
  useUnselectAll,
} from "react-admin";
import { findIndex } from "lodash";
import { delay } from "utils/tools";
import TableResultConfirmCommercialDocuments from "./TableResultConfirmCommercialDocuments";
import SaveIcon from "@material-ui/icons/Save";

interface IBulkActionProps {
  filteredData: any[];
  documentType: string;
}

interface IResults {
  id: number;
  description: string;
  type: string;
  status: string;
  processingStatus: string;
}

const useStyles = makeStyles({
  actionColor: {
    color: "#FFF",
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
});

const BulkActionUntagButton = ({
  filteredData,
  documentType,
}: IBulkActionProps) => {
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<IResults[]>([]);

  const refresh = useRefresh();
  const unselectAll = useUnselectAll();
  const provider = useDataProvider();
  const classes = useStyles();

  const selectedCommercialDocuments = filteredData.map((data) => ({
    id: data.id,
    description: data.items[0].description,
    type: "credit_note",
    status: data.status,
    processingStatus: "Not Processed",
  }));

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (finished) {
      unselectAll("commercialDocuments");
      refresh();
    }

    setOpen(false);
  };

  const untagCreditNote = (
    selectedId: number,
    index: number,
    retryCount: number = 1,
  ) => {
    return new Promise((resolve, reject) => {
      provider
        .customPatch(`commercialDocuments/${selectedId}/untag`, {
          data: {},
        })
        .then(() => {
          let tempInvoices = selectedCommercialDocuments;
          tempInvoices[index].status =
            tempInvoices[index].status === "draft" ? "draft" : "untagged";
          tempInvoices[index].processingStatus = "Finished";

          setResults([...tempInvoices]);
          resolve(true);
        })
        .catch(({ message }: any) => {
          retryCount++;

          if (retryCount <= 3) {
            // give random delay between 2 - 3 seconds
            const delay = Math.floor(Math.random() * 1000) + 2000;
            setTimeout(() => {
              untagCreditNote(selectedId, index, retryCount)
                .then(resolve)
                .catch(reject);
            }, delay);

            return;
          }

          let tempInvoices = selectedCommercialDocuments;
          tempInvoices[index].processingStatus = `Error, ${message}`;

          setResults([...tempInvoices]);
          reject();
        });
    });
  };

  const handleConfirm = async (selectedCommercialDocuments: IResults[]) => {
    await setProcessing(true);

    for (const selectedCommercialDocument of selectedCommercialDocuments) {
      selectedCommercialDocument.processingStatus = "Loading...";
      setResults([...selectedCommercialDocuments]);

      const index = findIndex(selectedCommercialDocuments, function (o) {
        return o.id === selectedCommercialDocument.id;
      });

      try {
        await untagCreditNote(selectedCommercialDocument.id, index);
      } catch (error) {
        continue;
      }

      await delay(2000);
    }

    await setFinished(true);
    await setProcessing(false);
  };

  return (
    <Fragment>
      <Button
        label="Untag Selected"
        onClick={handleOpen}
        style={{ backgroundColor: "#ffbf00" }}
      />
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          Untag Selected {documentType}s
        </DialogTitle>
        <DialogContent>
          {results.length > 0 ? (
            <TableResultConfirmCommercialDocuments records={results} />
          ) : (
            <DialogContentText color="initial">
              Are you sure you want to untag this selected{" "}
              {documentType.toLowerCase()}s?
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {!finished && (
            <Button
              onClick={() => handleConfirm(selectedCommercialDocuments)}
              color="primary"
              label="Confirm"
              disabled={processing}
            >
              <SaveIcon />
            </Button>
          )}
          <Button
            onClick={handleClose}
            color="secondary"
            label={`${finished ? "Finish" : "Back"}`}
            disabled={processing}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default BulkActionUntagButton;
